import shortid from 'shortid';
import { get } from 'lodash';
import { TRO_BOOK_PREFIX_AND_SUFFIX_TYPES, TRO_CONTENT_CDN } from '../constants/book';
import { TRC_QUESTION_TYPES } from '../constants/measure';
import {
    SET_TRC_CONTENT,
    NEXT_PAGE,
    RESET_ACTIVE_PAGE,
    RESET_PAGES_VIEWED,
    CLEAN_UP_BOOK,
    SET_LAST_RECORDED_PAGE,
} from './trcActions';

export const defaultState = {
    id: undefined,
    cover: undefined,
    title: undefined,
    introAudio: undefined,
    assessmentType: undefined,
    pages: [],
    pagesViewed: [],
    comprehensionItems: [],
    activePage: 0,
    indented: false,
    lastRecordedPage: undefined,
};

const OFFSET = 1;
const FIRST_PAGE = 0;

/**
 * Change book comprehension questions item from backend to frontend format
 * @example formatComprehensionItems([{
 *     item_idx: 123,
 *     item_type: 'MC',
 *     item_content: {
 *         responses: [
 *             { response_idx: 0, is_correct: false, text: 'Amy' },
 *             { response_idx: 1, is_correct: false, text: 'Barry' },
 *             { response_idc: 2, is_correct: false, text: 'Clara' },
 *             { response_idx: 3, is_correct: true, text: 'David' },
 *         ],
 *         question: 'What kind of names are these?',
 *     }
 * }],
 * }]) => [{
 *     id: 123,
 *     type: MC,
 *     possibleResponses: [
 *         { id: 0, text: 'Amy' },
 *         { id: 1, text: 'Barry' },
 *         { id: 2, text: 'Clara' },
 *         { id: 3, text: 'David' },
 *     ],
 *     stimulus: {
 *         text: 'What kind of names are these?',
 *     },
 * }]
 */
const formatComprehensionItems = comprehensionItems =>
    comprehensionItems.map(comprehensionItem => {
        const formattedComprehensionQuestion = {
            id: comprehensionItem.item_idx,
            type: comprehensionItem.item_type,
            possibleResponses: comprehensionItem.item_content.responses.map(response => ({
                id: response.response_idx,
                text: response.text,
                isCorrect: response.is_correct,
            })),
            stimulus: {
                text: comprehensionItem.item_content.question,
            },
        };

        if (comprehensionItem.item_type === TRC_QUESTION_TYPES.MULTIPLE_CORRECT_ANSWERS) {
            const INITIAL_MAX_RESPONSES = 0;
            const INCREMENT = 1;
            const getMaxPossibleResponses = (maxResponses, currentResponse) =>
                currentResponse.is_correct ? maxResponses + INCREMENT : maxResponses;
            formattedComprehensionQuestion.maxPossibleResponses = comprehensionItem.item_content.responses.reduce(
                getMaxPossibleResponses,
                INITIAL_MAX_RESPONSES,
            );
        }
        return formattedComprehensionQuestion;
    });

const getFullContentPath = path => {
    let troCDNUrl = TRO_CONTENT_CDN;
    if (process.env.NODE_ENV !== 'development' && get(window, 'amplify_lantern_config.troCDNUrl'))
        troCDNUrl = window.amplify_lantern_config.troCDNUrl;
    return troCDNUrl.concat(path);
};

const processPrefixes = (currentPages, prefix) => {
    const pages = currentPages;

    if (prefix) {
        prefix.forEach(({ type, value }) => {
            const image = value ? value.image : undefined;

            if (type === TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.PAGE_START)
                pages.push({
                    paragraphs: [],
                    template: value.page_type,
                    ...(image && {
                        picture: {
                            url: getFullContentPath(image.path),
                            altText: image.alt_text,
                            ...(image.caption && { caption: image.caption }),
                        },
                    }),
                    wordCount: 0,
                });

            const lastPageIndex = pages.length - 1;

            if (type === TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.PARAGRAPH_START)
                pages[lastPageIndex].paragraphs.push({
                    text: value.style === 'header' ? '{{break}}' : '',
                    id: shortid.generate(),
                });

            const { paragraphs } = pages[lastPageIndex];
            const lastParagraphIndex = paragraphs.length - 1;

            switch (type) {
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.TITLE:
                    pages[lastPageIndex].paragraphs[lastParagraphIndex].header = value;
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.TEXT:
                    {
                        const lastParagraph = paragraphs[lastParagraphIndex];
                        lastParagraph.text += value;
                    }
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.STRESS_START:
                    {
                        const lastParagraph = paragraphs[lastParagraphIndex];
                        lastParagraph.text += '{{stress_start}}';
                    }
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.STRESS_END:
                    {
                        const lastParagraph = paragraphs[lastParagraphIndex];
                        lastParagraph.text += '{{stress_end}}';
                    }
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.BREAK:
                    {
                        const lastParagraph = paragraphs[lastParagraphIndex];
                        lastParagraph.text += '{{break}}';
                    }
                    break;
                default:
                    break;
            }
        });
    }
    return pages;
};

const processWordAndSuffixes = (currentPages, suffix, word) => {
    const pages = currentPages;
    const LAST_PAGE_INDEX = pages.length - 1;
    const LAST_PARAGRAPH_INDEX = pages[LAST_PAGE_INDEX].paragraphs.length - 1;
    const lastParagraph = pages[LAST_PAGE_INDEX].paragraphs[LAST_PARAGRAPH_INDEX];
    pages[LAST_PAGE_INDEX].wordCount += 1;

    lastParagraph.text += word;
    if (suffix) {
        suffix.forEach(({ type, value }) => {
            switch (type) {
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.TEXT:
                    lastParagraph.text += value;
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.STRESS_START:
                    lastParagraph.text += '{{stress_start}}';
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.STRESS_END:
                    lastParagraph.text += '{{stress_end}}';
                    break;
                case TRO_BOOK_PREFIX_AND_SUFFIX_TYPES.BREAK:
                    lastParagraph.text += '{{break}}';
                    break;
                default:
                    break;
            }
        });
    }
    lastParagraph.text += ' ';

    return pages;
};

const calculateLastWordIndices = currentPages =>
    (currentPages || []).map((page, pageNumber) => {
        const currentPage = page;
        const { wordCount } = page;
        if (pageNumber === FIRST_PAGE) {
            currentPage.lastWordIndexOnPage = wordCount - OFFSET;
        } else {
            const prevPage = currentPages[pageNumber - 1];
            currentPage.lastWordIndexOnPage = prevPage.lastWordIndexOnPage + wordCount;
        }
        return currentPage;
    });

const generatePages = content => {
    let pages = [];
    content.forEach(({ word, prefix, suffix }) => {
        pages = processPrefixes(pages, prefix);
        pages = processWordAndSuffixes(pages, suffix, word);
    });
    pages = calculateLastWordIndices(pages);
    return pages;
};

const bookReducer = (state = defaultState, action) => {
    switch (action.type) {
        case SET_TRC_CONTENT:
            return {
                ...state,
                id: action.content.book_id,
                cover: getFullContentPath(action.content.cover_path),
                coverAltText: action.content.cover_alt_text,
                title: action.content.title,
                genre: action.content.genre,
                relativeLevel: action.content.relative_level,
                introAudio: getFullContentPath(action.content.intro_audio_path),
                pages: generatePages(action.content.content),
                comprehensionItems: formatComprehensionItems(action.content.comprehension_items),
                indented: action.content.indentation,
                lastRecordedPage: undefined,
            };
        case CLEAN_UP_BOOK:
            return defaultState;
        case NEXT_PAGE: {
            const { activePage, pagesViewed } = state;
            const { incrementation } = action;
            const activePageIndex = activePage + incrementation;
            const currentPages = pagesViewed.length ? pagesViewed : [activePage + 1];
            const nextViewedPage = activePageIndex + Math.abs(incrementation);
            return {
                ...state,
                activePage: activePageIndex,
                pagesViewed: [...currentPages, nextViewedPage],
            };
        }
        case RESET_ACTIVE_PAGE:
            return {
                ...state,
                activePage: 0,
                pagesViewed: [],
            };
        case SET_LAST_RECORDED_PAGE:
            return {
                ...state,
                lastRecordedPage: state.activePage,
            };
        case RESET_PAGES_VIEWED:
            return {
                ...state,
                pagesViewed: [],
            };
        default:
            return state;
    }
};

export default bookReducer;
